import { useTranslation } from 'react-i18next';
import { Sidepanel } from '@/components/layout/Sidepanel';
import { FirmwareCreateView } from './FirmwareCreateView';
import { FirmwareDraftView } from './FirmwareDraftView';
import { FirmwareSurpassedView } from './FirmwareSurpassedView';
import { FirmwareActiveView } from './FirmwareActiveView';
import type { Firmware, IdNameType, Country } from '../../types';
import type { EditableDropdownItem } from '@/components/EditableDropdown';
import { SidebarContent } from '@/components/layout/sidepanel/SidebarFooter';

export type FirmwareSidePanelContent =
  | { type: 'create' }
  | { type: 'view'; firmware: Firmware };

interface FirmwareSidePanelProps {
  content: FirmwareSidePanelContent | null;
  panelKey: string | null;
  isOpen: boolean;
  onClose: () => void;
  modelId: number;
  firmwareTypes: IdNameType[];
  countries: Country[];
  firmwareTypeItems: EditableDropdownItem[];
  currentTypeId?: number;
  currentCountryId?: string;
}

export function FirmwareSidePanelHandler({
  content,
  panelKey,
  isOpen,
  onClose,
  modelId,
  firmwareTypes,
  countries,
  firmwareTypeItems,
  currentTypeId,
  currentCountryId,
}: FirmwareSidePanelProps) {
  const { t } = useTranslation();

  if (!content || !panelKey) return null;

  const getTitle = () => {
    if (content.type === 'create') {
      return t('firmware.sidePanel.createTitle');
    }
    const fw = content.firmware;
    switch (fw.state) {
      case 'Draft':
        return t('firmware.sidePanel.draftTitle');
      case 'Surpassed':
        return t('firmware.sidePanel.surpassedTitle');
      default:
        return fw.versionString;
    }
  };

  return (
    <Sidepanel
      key={panelKey}
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      className="sm:max-w-[300px]"
    >
      <SidebarContent>
        <div className="mt-4 w-full sm:px-5 space-y-5">
          {content.type === 'create' && (
            <FirmwareCreateView
              modelId={modelId}
              firmwareTypes={firmwareTypes}
              countries={countries}
              initialTypeId={currentTypeId}
              initialCountryId={currentCountryId}
              onCreated={onClose}
            />
          )}
          {content.type === 'view' && content.firmware.state === 'Draft' && (
            <FirmwareDraftView
              modelId={modelId}
              firmware={content.firmware}
              firmwareTypeItems={firmwareTypeItems}
              onReleased={onClose}
              onDeleted={onClose}
            />
          )}
          {content.type === 'view' && content.firmware.state === 'Surpassed' && (
            <FirmwareSurpassedView
              modelId={modelId}
              firmware={content.firmware}
              firmwareTypeItems={firmwareTypeItems}
              onRolledBack={onClose}
              onDeleted={onClose}
            />
          )}
          {content.type === 'view' && content.firmware.state === 'Active' && (
            <FirmwareActiveView
              firmware={content.firmware}
              firmwareTypeItems={firmwareTypeItems}
            />
          )}
        </div>
      </SidebarContent>
    </Sidepanel>
  );
}
